import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React from "react";
import style from "./style";
import { useNavigate } from "react-router-dom";
import color from "../../../../../constants/color";
import { paymentTermsOptions } from "../../../../../constants/data";


const EditRecurringTemplatePage = ({
  template,
  customers,
  products,
  productInformation,
  handleFieldChange,
  handleProductChange,
  handleAddProduct,
  handleRemoveProduct,
  handleUpdateRecurringTemplate,
  total,
  discountValue,
  taxValue,
  grandTotal,
  isFormValid,
}) => {
  if (!template) return <>Loading...</>;

  const navigate = useNavigate();

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>
            Edit Recurring Template
          </Typography>
        </Box>
      </Box>

      <Grid container sx={style.mainContainer}>
        <Grid item xs={12} lg={11.8} sx={style.gridbox}>
          {/* Customer */}
          <Box sx={style.inputSection}>
            <Box sx={{ ...style.inputBox, width: "100%" }}>
              <Typography sx={style.inputText}>
                Customer<span style={{ color: "red" }}>*</span>
              </Typography>
              <Autocomplete
                options={[
                  { _id: "add_new", name: "+ New Customer" }, ...(customers || [])]}
                getOptionLabel={(option) => option.name || ""}
                value={template.customer}
                onChange={(_, newValue) =>{
                  if (!newValue) {
                    // handle clear
                    handleFieldChange("customer", null);
                    // Clear addresses when customer is cleared
                    handleFieldChange("billingAddress", {
                      country: "",
                      street1: "",
                      street2: "",
                      city: "",
                      state: "",
                      pinCode: "",
                      phone: "",
                    });
                    handleFieldChange("shippingAddress", {
                      country: "",
                      street1: "",
                      street2: "",
                      city: "",
                      state: "",
                      pinCode: "",
                      phone: "",
                    });
                    return;
                  }

                  if (newValue?._id === "add_new") {
                    navigate("/dashboard/addNewCustomer"); // redirect page
                    return;
                  }

                  // Populate addresses when customer is selected
                  const billingAddress = newValue.billingAddress || {
                    country: "",
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    pinCode: "",
                    phone: "",
                  };

                  const shippingAddress = newValue.shippingAddress || {
                    country: "",
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    pinCode: "",
                    phone: "",
                  };

                  handleFieldChange("customer", newValue);
                  handleFieldChange("billingAddress", billingAddress);
                  handleFieldChange("shippingAddress", shippingAddress);
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={
                      option._id === "add_new"
                        ? { color: color.secondary }
                        : {}
                    }
                  >
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{ ...style.modalInput, ...style.inputSpace }}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Billing & Shipping */}
          <Grid container>
            <Grid
              item
              xs={12}
              lg={11.7}
              sx={{
                ...style.gridbox,
                my: 0,
                mr: { xs: 0, sm: 0, md: 0, lg: 0 },
              }}
            >
              <Box sx={style.addressBox}>
                <Box sx={{ width: "50%" }}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Billing Address
                  </Typography>
                  {Object.keys(template.billingAddress || {}).map((key) => (
                    <Box key={key} sx={style.addressInput}>
                      <Box sx={{ width: "20%" }}>
                        <Typography
                          sx={{ ...style.inputText, fontSize: "14px" }}
                        >
                          {key}
                        </Typography>
                      </Box>
                      <Box sx={{ width: "75%" }}>
                        <TextField
                          key={key}
                          value={template.billingAddress[key]}
                          onChange={(e) =>
                            handleFieldChange("billingAddress", {
                              ...template.billingAddress,
                              [key]: e.target.value,
                            })
                          }
                          sx={style.addressTextField}
                          fullWidth
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ width: "50%" }}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Shipping Address
                  </Typography>
                  {Object.keys(template.shippingAddress || {}).map((key) => (
                    <Box key={key} sx={style.addressInput}>
                      <Box sx={{ width: "20%" }}>
                        <Typography
                          sx={{ ...style.inputText, fontSize: "14px" }}
                        >
                          {key}
                        </Typography>
                      </Box>
                      <Box sx={{ width: "75%" }}>
                        <TextField
                          key={key}
                          value={template.shippingAddress[key]}
                          onChange={(e) =>
                            handleFieldChange("shippingAddress", {
                              ...template.shippingAddress,
                              [key]: e.target.value,
                            })
                          }
                          sx={style.addressTextField}
                          fullWidth
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Profile Name, Repeat Every, and Payment Terms */}
          <Box sx={style.inputSection}>
            {/* Profile Name */}
            <Box sx={style.inputBox}>
              <Typography sx={style.inputText}>
                Profile Name<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                value={template.profileName}
                onChange={(e) =>
                  handleFieldChange("profileName", e.target.value)
                }
                fullWidth
                sx={{
                  ...style.addressTextField,
                  borderRadius: "10px",
                }}
              />
            </Box>

            {/* Repeat Every */}
            <Box sx={style.inputBox}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "2.5em",
                    borderRadius: "10px",
                  },
                }}
              >
                <Typography sx={style.inputText}>
                  Repeat Every<span style={{ color: "red" }}>*</span>
                </Typography>
                <Select
                  value={template.repeatEvery}
                  onChange={(e) =>
                    handleFieldChange("repeatEvery", e.target.value)
                  }
                  fullWidth
                  displayEmpty
                >
                  <MenuItem value="">Select Repeat Frequency</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Payment Terms */}
            <Box sx={style.inputBox}>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "2.5em",
                    borderRadius: "10px",
                  },
                }}
              >
                <Typography sx={style.inputText}>
                  Payment Terms<span style={{ color: "red" }}>*</span>
                </Typography>
                <Select
                  value={template.paymentTerms}
                  onChange={(e) =>
                    handleFieldChange("paymentTerms", e.target.value)
                  }
                  fullWidth
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#aaa" }}>
                        Select Payment Terms
                      </span>
                    )
                  }
                >
                  {paymentTermsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Start On, End On, and Daily Penalty Amount */}
          <Box sx={style.inputSection}>
            {/* Start On */}
            <Box sx={style.inputBox}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={style.label}>
                  Start on<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <DatePicker
                  value={dayjs(template.startOn)}
                  onChange={(date) =>
                    handleFieldChange("startOn", date?.toISOString())
                  }
                  sx={style.dateInput}
                  slotProps={{
                    textField: { fullWidth: true },
                    field: {
                      readOnly: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* End On */}
            <Box sx={style.inputBox}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputLabel sx={style.label}>
                  End on<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <DatePicker
                  // label="End On"
                  value={dayjs(template.endOn)}
                  minDate={dayjs(template.startOn)}
                  onChange={(date) =>
                    handleFieldChange("endOn", date?.toISOString())
                  }
                  sx={style.dateInput}
                  slotProps={{
                    textField: { fullWidth: true },
                    field: {
                      readOnly: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Daily Penalty Amount */}
            <Box sx={style.inputBox}>
              <Typography sx={style.inputText}>
                Daily Penalty<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                type="number"
                value={template.dailyPenaltyAmount}
                onChange={(e) =>
                  handleFieldChange("dailyPenaltyAmount", e.target.value)
                }
                fullWidth
                sx={{
                  ...style.addressTextField,
                  borderRadius: "10px",
                }}
              />
            </Box>
          </Box>

          {/* Other Information */}
          <Box sx={style.inputSection}>
            <Box sx={{ ...style.inputBox, width: "100%" }}>
              <Typography sx={style.inputText}>Other Information</Typography>
              <TextField
                value={template.otherInformation}
                onChange={(e) =>
                  handleFieldChange("otherInformation", e.target.value)
                }
                fullWidth
                placeholder="(Optional)"
                sx={{
                  ...style.addressTextField,
                  borderRadius: "10px",
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Product Information */}
        <Box sx={style.itemSection}>
          <Box
            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
          ></Box>
          <Typography sx={style.gridText}>
            Product Informations<span style={{ color: "red" }}>*</span>
          </Typography>
        </Box>

        <Grid item xs={12} lg={12} sx={style.gridbox}>
          {productInformation.map((product, idx) => { 
            const stockQuantity = products.find((p) => p._id === product.productId)?.quantity || 0;
            return (
            <Box sx={style.leaveSection} key={idx}>
              <Grid container sx={style.itemGrid} >
                <Grid item xs={12} lg={2.5}>
                <Autocomplete
                  options={[{ _id: "add_new", title: "+ New Product" }, ...(products || [])]}
                  getOptionLabel={(option) => option.title || ""}
                  value={
                    products.find((p) => p._id === product.productId) || null
                  }
                  onChange={(_, newVal) =>{
                    if (newVal?._id === "add_new") {
                      navigate("/dashboard/addNewItem"); // redirect page
                      return;
                    }
                    handleProductChange(idx, "productId", newVal?._id || "")}
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Product Name"
                      sx={{ ...style.modalInput, ...style.inputSpace }}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  placeholder="Description"
                  value={product.description}
                  onChange={(e) =>
                    handleProductChange(idx, "description", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      my: 1,
                      fontSize: "14px",
                      fontWeight: "400",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  placeholder="Unit Cost"
                  type="number"
                  value={product.unitCost}
                  onChange={(e) =>
                    handleProductChange(idx, "unitCost", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    sx: {
                      borderRadius: "8px",
                      my: 1,
                      fontSize: "14px",
                      fontWeight: "400",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
              <Box
                  display="flex"
                  alignItems="center"
                  sx={{ ...style.customInput, width: "100%" }}
                >
                  <TextField
                    type="number"
                    value={product.quantity}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      const maxQuantity = stockQuantity;
                      if (e.target.value === "") {
                        handleProductChange(idx, "quantity", "");
                        return;
                      }

                      if (isNaN(value)) return;
                      const clampedValue = Math.min(Math.max(0, value), maxQuantity);
                      handleProductChange(idx, "quantity", clampedValue)
                    }}
                    fullWidth
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: stockQuantity
                      },
                      // sx: {
                      //   borderRadius: "8px",
                      //   my: 1,
                      //   fontSize: "14px",
                      //   fontWeight: "400",
                      // },
                      endAdornment: (
                        <InputAdornment position="end" variant="filled">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              bgcolor: "#DEDEDE",
                              height: 43,
                              p: 0,
                              width: 70, // Ensure minimum width
                              // maxWidth: 120, // Prevent excessive width
                              ...style.inputBgBox,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 21,
                                maxWidth: 80, // Limit width to prevent overflow
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "600",
                                userSelect: "none",
                                borderRadius: "8px",
                                fontSize: "14px", // Smaller font for long numbers
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                px: 0.8,
                              }}
                            >
                              {
                                stockQuantity
                              }
                            </Box>
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    p: 1,
                                    borderRadius: "6px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      color: "#373D3F",
                                      mb: 1,
                                    }}
                                  >
                                    Stock Details
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      color: "#373D3F",
                                      fontWeight: 400,
                                    }}
                                  >
                                    Stock on Hand :{" "}
                                    { 
                                      stockQuantity || "-"
                                    }                                    
                                  </Typography>
                                </Box>
                              }
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "white !important",
                                    p: 0,
                                    border:
                                      "1px solid #878787 !important",
                                    boxShadow: "none !important",
                                    "& .MuiTooltip-arrow": {
                                      color: "white !important",
                                      "&::before": {
                                        border: "1px solid #878787",
                                        backgroundColor:
                                          "white !important",
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                edge="end"
                                sx={{
                                  color: "#878787",
                                  padding: "4px",
                                }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  type="number"
                  value={product.amount}
                  disabled
                  fullWidth
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      my: 1,
                      fontSize: "14px",
                      fontWeight: "400",
                    },
                  }}
                />
              </Grid>
              {idx === 0 && (
                <Grid item xs={1.5} lg={0.5}>
                  <Box
                    sx={{
                      ...style.addIcon,
                      display: "flex",
                      justifyContent: "center",
                    }}
                    onClick={handleAddProduct}
                  >
                    +
                  </Box>
                </Grid>
              )}
              {idx !== 0 && (
                <Grid item xs={1.5} lg={0.5}>
                  <Box
                    sx={{...style.deleteIcon,
                      display: "flex",
                      justifyContent: "center"
                    }}
                    onClick={() => handleRemoveProduct(idx)}
                  >
                    <DeleteOutlineIcon />
                  </Box>
                </Grid>
              )}
            </Grid>
            </Box>
          )})}
        </Grid>

        {/* Discount and Tax */}
        <Box sx={style.itemSection}>
          <Box
            sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
          ></Box>
          <Typography sx={style.gridText}>Discount and Tax(%)</Typography>
        </Box>

        <Grid container sx={style.lastGrid}>
          {/* Discount and Tax */}
          <Grid item xs={12} lg={7.6} sx={style.gridboxs}>
            <Grid container>
              <Box sx={{ ...style.inputSection, width: "100%", gap: 3.5 }}>
                <Box sx={{ ...style.inputBox, width: "50%" }}>
                  <Typography sx={style.inputText}>
                    Discount<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={template.discountPercentage}
                    onChange={(e) =>
                      handleFieldChange("discountPercentage", e.target.value)
                    }
                    fullWidth
                    InputProps={{
                      inputProps: {
                        min: 0,
                      },
                      sx: {
                        borderRadius: "8px",
                        my: 1,
                        fontSize: "16px",
                        fontWeight: "400",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ ...style.inputBox, width: "50%" }}>
                  <Typography sx={style.inputText}>Tax</Typography>
                  <TextField
                    type="number"
                    value={template.taxPercentage}
                    onChange={(e) =>
                      handleFieldChange("taxPercentage", e.target.value)
                    }
                    fullWidth
                    InputProps={{
                      inputProps: {
                        min: 0,
                      },
                      sx: {
                        borderRadius: "8px",
                        my: 1,
                        fontSize: "16px",
                        fontWeight: "400",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Total */}
          <Grid
            item
            xs={12}
            lg={4.2}
            sx={{ ...style.gridboxs, marginTop: "-17px !important" }}
          >
            <Box sx={style.leaveSection}>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Total</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>{total}</Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Discount</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>
                    -{discountValue} ({template.discountPercentage}%)
                  </Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>Tax</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueText}>
                    +{taxValue} ({template.taxPercentage}%)
                  </Typography>
                </Box>
              </Box>
              <Box sx={style.inputContainerBox}>
                <Box sx={style.key}>
                  <Typography sx={style.descValueTexts}>Grand Total</Typography>
                </Box>
                <Box sx={style.key}>
                  <Typography sx={style.descValueTexts}>
                    {grandTotal}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Save Changes Button */}
        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              sx={style.cancelButtons}
              disabled={!isFormValid()}
              onClick={handleUpdateRecurringTemplate}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default EditRecurringTemplatePage;
